/**
 * In-memory setup checklist for the mock API client (tracker item 75). Same
 * rules as the backend where the UI depends on them: ten steps in order,
 * data-backed steps are done when their data exists (the host client supplies
 * those facts), the two manual steps follow Mark done, skipping a done step is
 * 409 SETUP_STEP_DONE, Mark done only on manual steps (400), hide / show.
 * Existing mock tenants start hidden, like the migration backfill, so the
 * default demo looks as before; `show(tenantId)` starts a tenant fresh.
 */
import * as types from './types';

export class MockSetupError extends Error {
  constructor(public status: number, message: string, public code: string) {
    super(message);
  }
}

/** Facts about a tenant's data, one flag per data-backed step (the payments step is `onlinePayments`). */
export interface MockSetupSignals {
  company_profile?: boolean;
  tax_rate?: boolean;
  items?: boolean;
  price_chart?: boolean;
  users?: boolean;
  email_sender?: boolean;
  onlinePayments?: boolean;
  portal?: boolean;
}

interface StepDefinition {
  key: types.SetupStepKey;
  title: string;
  optional: boolean;
  manual: boolean;
  link: string;
}

const STEPS: StepDefinition[] = [
  { key: 'company_profile', title: 'Add your company details', optional: false, manual: false, link: '/settings/company' },
  { key: 'timezone_hours', title: 'Set your timezone and working hours', optional: false, manual: true, link: '/settings/company' },
  { key: 'tax_rate', title: 'Add a tax rate', optional: false, manual: false, link: '/settings/tax-rates' },
  { key: 'payment_terms', title: 'Confirm your payment terms', optional: false, manual: true, link: '/settings/company' },
  { key: 'items', title: 'Add your services and products', optional: false, manual: false, link: '/settings/items' },
  { key: 'price_chart', title: 'Add a price chart', optional: true, manual: false, link: '/settings/price-charts' },
  { key: 'users', title: 'Invite your team', optional: false, manual: false, link: '/settings/users' },
  { key: 'email_sender', title: 'Connect an email sender', optional: false, manual: false, link: '/settings/notifications' },
  { key: 'square', title: 'Connect online payments', optional: false, manual: false, link: '/settings/payments' },
  { key: 'portal', title: 'Set up the customer portal', optional: true, manual: false, link: '/settings/customer-portal' },
];

interface TenantState {
  skipped: Set<types.SetupStepKey>;
  done: Set<types.SetupStepKey>;
  dismissed: boolean;
}

export class MockSetup {
  private states = new Map<string, TenantState>();

  constructor(private readonly signals: (tenantId: string) => MockSetupSignals) {}

  private state(tenantId: string): TenantState {
    let s = this.states.get(tenantId);
    if (!s) {
      s = { skipped: new Set(), done: new Set(), dismissed: true };
      this.states.set(tenantId, s);
    }
    return s;
  }

  /** Start a tenant's checklist fresh and visible (what a tenant created after the feature sees). */
  show(tenantId: string): void {
    this.states.set(tenantId, { skipped: new Set(), done: new Set(), dismissed: false });
  }

  private definition(key: string): StepDefinition {
    const step = STEPS.find((s) => s.key === key);
    if (!step) throw new MockSetupError(404, 'Unknown setup step', 'SETUP_UNKNOWN_STEP');
    return step;
  }

  private isDone(step: StepDefinition, tenantId: string, s: TenantState): boolean {
    if (step.manual) return s.done.has(step.key);
    const signals = this.signals(tenantId);
    return !!(step.key === 'square' ? signals.onlinePayments : signals[step.key as keyof MockSetupSignals]);
  }

  get(tenantId: string): types.SetupProgress {
    const s = this.state(tenantId);
    const steps: types.SetupStep[] = STEPS.map((step) => {
      const done = this.isDone(step, tenantId, s);
      return { ...step, done, skipped: !done && s.skipped.has(step.key) };
    });
    return {
      steps,
      dismissed: s.dismissed,
      doneCount: steps.filter((x) => x.done).length,
      resolvedCount: steps.filter((x) => x.done || x.skipped).length,
      total: steps.length,
      complete: steps.every((x) => x.optional || x.done || x.skipped),
    };
  }

  skip(tenantId: string, key: string): types.SetupProgress {
    const step = this.definition(key);
    const s = this.state(tenantId);
    if (this.isDone(step, tenantId, s)) throw new MockSetupError(409, 'This step is already done', 'SETUP_STEP_DONE');
    s.skipped.add(step.key);
    return this.get(tenantId);
  }

  unskip(tenantId: string, key: string): types.SetupProgress {
    this.state(tenantId).skipped.delete(this.definition(key).key);
    return this.get(tenantId);
  }

  markDone(tenantId: string, key: string): types.SetupProgress {
    const step = this.manualStep(key);
    const s = this.state(tenantId);
    s.done.add(step.key);
    s.skipped.delete(step.key);
    return this.get(tenantId);
  }

  unmarkDone(tenantId: string, key: string): types.SetupProgress {
    this.state(tenantId).done.delete(this.manualStep(key).key);
    return this.get(tenantId);
  }

  setDismissed(tenantId: string, dismissed: boolean): types.SetupProgress {
    this.state(tenantId).dismissed = dismissed;
    return this.get(tenantId);
  }

  private manualStep(key: string): StepDefinition {
    const step = this.definition(key);
    if (!step.manual) {
      throw new MockSetupError(400, 'This step is completed by setting it up, not by marking it done', 'SETUP_STEP_NOT_MANUAL');
    }
    return step;
  }
}
